import { StateProviders } from '@candulabs/core';
import { SerializedNode } from '@candulabs/craft';
import omit from 'lodash/omit';
import pickBy from 'lodash/pickBy';

import { replaceAll } from 'commons/utils';
import { CRAFT_ROOT_ID } from 'craft/constants';
import { parseProviders, createVariableString } from 'craft/containers/Actions/parseVariables';
import { StateProviderType } from 'craft/containers/Craft/format/insertStateForVariable';
import { ContentNode, Variable, DocumentPlacements, Action } from 'craft/types';
import { unpackShorthandStyles } from 'craft/utils';

import { migrateMediaHiddenSelectors } from './migrateMediaHiddenSelectors';

type NodeProps = Record<string, string | number | boolean>;

const variableType = (resolverType: StateProviderType) => {
  switch (resolverType) {
    case StateProviderType.UserTraits:
      return 'user';
    case StateProviderType.URL:
      return 'url';
    default:
      throw new Error('Unknown resolver type');
  }
};

const readProps = (
  { children, ...otherProps }: NodeProps,
  variables?: Record<string, Variable>,
  stateProviders?: StateProviders,
) => {
  // read variables from props and replace them with their values
  if (!variables || !stateProviders) {
    return pickBy({
      childrenString: children,
      ...otherProps,
    });
  }

  const overrideProps: Record<string, string> = {};
  ['src', 'href'].forEach((propName) => {
    if (!otherProps?.[propName] || typeof otherProps[propName] !== 'string') {
      return;
    }
    const propValue = otherProps[propName] as string;

    const variableIds = parseProviders(propValue);
    overrideProps[propName] = variableIds.reduce((acc, id) => {
      const variable = variables[id];

      const provider = stateProviders[variable?.providerId];
      if (!variable || !provider) {
        return acc;
      }

      const { type, traits, key } = provider.resolver;
      return replaceAll(
        acc,
        `{{${id}}}`,
        createVariableString(variableType(type), traits ? traits[0] : key, variable.fallback),
      );
    }, propValue);
  });

  // Because children is a reserved keyword in Craft, we rename it to `childrenString` in order
  // not to conflict with Craft's internal logic handling for children.
  return pickBy({
    childrenString: children,
    ...otherProps,
    ...overrideProps,
  });
};

interface NodeWithVariables extends ContentNode {
  variables: Record<string, Variable>;
  props: {
    children: string;
  };
}

/**
 * Checks if a node should contain variable NODES
 * NOTE: Only text nodes render variables in-editor as nodes
 * @param node
 */
const hasVariableChildren = (node: ContentNode): node is NodeWithVariables => {
  if (node.type === 'Text' && Object.keys(node.variables || {}).length > 0) {
    if (typeof node.props?.children !== 'string') {
      throw new Error('Expected variable node to have string child');
    }

    return true;
  }
  return false;
};

const documentVariableToCraftVariable = (documentVariable: any, stateProviders: StateProviders) => {
  if (!documentVariable) {
    return undefined;
  }

  return Object.keys(documentVariable)
    .map((id) => {
      const variable = documentVariable[id];
      const provider = stateProviders[variable.providerId];

      if (variable.location.type === 'Data') {
        // we shorthand these, no need to keep them as a variable as far as craft in the craft model
        return null;
      }

      // catch in case we didn't get a properly formed state provider, this shouldn't happen
      if (!provider) {
        return null;
      }

      const { traits, key } = provider.resolver;
      return {
        id,
        name: traits ? traits[0] : key,
        fallback: variable.fallback,
      };
    })
    .filter((variable) => !!variable);
};

const documentVariableToCraftNodes = (
  id: string,
  node: NodeWithVariables,
  stateProviders: StateProviders = {},
) => {
  const { data, props, css, states, variables } = node;

  const match = props.children.match(/^{{([a-zA-Z0-9-_]+)}}$/);

  if (!match) {
    throw new Error('Could not parse variable from node');
  }

  const [, variableId] = match;
  if (!variableId || !variables[variableId]) {
    throw new Error('Unrecognised variable ID');
  }

  const type = 'Variable';
  const provider = stateProviders[variables[variableId].providerId];

  if (!provider) {
    throw new Error(`Missing provider for variable ${variableId}`);
  }

  const name = provider.resolver.traits[0];

  const childId = `${id}-var`;

  const variableNode = {
    type: { resolvedName: type },
    isCanvas: false,
    displayName: type,
    name: type,
    props: {},
    custom: {
      actions: data?.actions,
      custom: data?.custom,
      conditionals: data?.conditionals,
      variable: {
        id: variableId,
        fallback: variables[variableId]?.fallback,
        name,
      },
      css: css || {},
      states,
    },
    nodes: [childId],
    linkedNodes: {},
  };

  const childNode = {
    type: {
      resolvedName: 'Text',
    },
    isCanvas: false,
    props: {
      childrenString: name,
    },
    displayName: 'Text',
    name: 'Text',
    custom: {
      custom: {
        marks: [],
      },
      css: {},
    },
    nodes: [],
    linkedNodes: {},
  };

  return {
    [id]: variableNode,
    [childId]: childNode,
  };
};

const inlineActionVariables = (
  actions?: Action[],
  variables?: Record<string, Variable>,
  stateProviders?: StateProviders,
): Action[] | undefined => {
  if (!actions || !variables || !stateProviders) {
    return actions;
  }
  return actions.map((action) => {
    // In the future we could consider looping over variables rather than actions.handler.href explicitly,
    // we can then use the path to make things less href specific
    // @ts-ignore
    const href = action?.handler?.href as string;
    if (!href) {
      return action;
    }

    const variableIds = parseProviders(href);
    const newHref = variableIds.reduce((acc, id) => {
      const variable = variables[id];

      const provider = stateProviders[variable?.providerId];
      if (!variable || !provider) {
        return acc;
      }

      const { type, traits, key } = provider.resolver;
      return replaceAll(
        acc,
        `{{${id}}}`,
        createVariableString(variableType(type), traits ? traits[0] : key, variable.fallback),
      );
    }, href);

    return {
      ...action,
      handler: {
        ...action.handler,
        href: newHref,
      },
    };
  }) as Action[];
};

export const documentNodeToCraftNode = (
  id: string,
  node: ContentNode,
  stateProviders: StateProviders = {},
  placements?: DocumentPlacements,
): Record<string, Omit<SerializedNode, 'hidden' | 'parent'>> => {
  const { data, props, states, variables, collections, cssSelectors } = node;
  let { css } = node;
  const { type, nodes } = node;

  const craftProps = (props && readProps(props, variables, stateProviders)) || {};

  // Text nodes need to become Variable nodes for RTE
  if (hasVariableChildren(node)) {
    return documentVariableToCraftNodes(id, node, stateProviders);
  }

  // prior to rendering, gridTemplateColumns css prop should be deleted for legacy content to have it,
  // otherwise it overrides the classNames defined for column wrapping in the editor
  if (type === `Columns` && css?.gridTemplateColumns) {
    css = omit(css, 'gridTemplateColumns');
  }

  return {
    [id]: {
      type: { resolvedName: type },
      isCanvas: id === CRAFT_ROOT_ID,
      name: type,
      props: craftProps,
      custom: migrateMediaHiddenSelectors(
        {
          actions: inlineActionVariables(data?.actions, variables, stateProviders),
          custom: data?.custom,
          displayName: data?.displayName,
          media: data?.media,
          css: unpackShorthandStyles(css || {}),
          // @ts-ignore
          cssSelectors: cssSelectors || {},
          variables: documentVariableToCraftVariable(variables, stateProviders),
          states,
          placements: id === CRAFT_ROOT_ID ? placements : undefined,
          collections,
          conditionals: data?.conditionals,
        },
        type,
      ),
      nodes: nodes || [],
      linkedNodes: {},
    },
  };
};
